// Lib
import Handsontable from 'handsontable';
import { Events } from 'handsontable/pluginHooks';
import { useCallback, useEffect } from 'react';
import { useHotTableDispatch } from './store/hotTableStoreHooks';
import { setActiveTable } from './store/hotTableStoreActions';
import rafThrottle from '../../../common/utils/lib/rafThrottle';

const TRACKED_HOOKS: (keyof Events)[] = [
    'afterChange',
    'afterCreateRow',
    'afterCreateCol',
    'afterRemoveRow',
    'afterRemoveCol',
    'afterRowMove',
    'afterColumnMove',
    'afterColumnResize',
    'afterSelection',
    'afterSelectionEnd',
    'afterDeselect',
];

/**
 * Stores the current active Handsontable instance in the HotTable store & clears it when no longer edited.
 */
export const useSyncActiveTableState = (tableInstance: Handsontable | null, isActive: boolean) => {
    const hotTableDispatch = useHotTableDispatch();
    const dispatchUpdateActiveTable = useCallback(
        (activeTable: Handsontable | null) => hotTableDispatch(setActiveTable(activeTable)),
        [],
    );

    useEffect(() => {
        if (!tableInstance) return;
        if (!isActive) return;

        dispatchUpdateActiveTable(tableInstance);

        const throttledUpdateActiveTable = rafThrottle(() => dispatchUpdateActiveTable(tableInstance));

        TRACKED_HOOKS.forEach((hook) => tableInstance.addHook(hook, throttledUpdateActiveTable));

        return () => {
            // You shouldn't be able to immediately switch from editing one table to another, or edit
            //  two tables at once, so this should be a safe operation (i.e. won't clobber another active table).
            dispatchUpdateActiveTable(null);

            throttledUpdateActiveTable.cancel();

            TRACKED_HOOKS.forEach((hook) => tableInstance.removeHook(hook, throttledUpdateActiveTable));
        };
    }, [tableInstance, isActive]);
};
